import { AUTO_CREDIT_CITY_STORAGE_KEY } from '@/shared/constants/webview-services';
import { safeLocalStorage } from '@/shared/services/helpers/DOM';

import {
  type ICloseReason,
  type ISystemActionPayloads,
  type ITabBarConfig,
  SYSTEM_ACTIONS,
  type TBridgeNotificationType,
  type TBridgeScalar,
  type THostPage,
  isHostPage
} from '../protocol';

/**
 * Управление «хромом» сервиса (заголовок, таб-бар, состояние «назад»).
 * Реализацию прокидывает хост; методы опциональны (no-op, если не заданы).
 */
export interface IBridgeHostChrome {
  setTitle?(title: string): void;
  setTabBarVisible?(visible: boolean): void;
  setTabBar?(config: ITabBarConfig): void;
  setCanGoBack?(value: boolean): void;
}

export interface IBridgeHostNavigation {
  navigate?(route: string, params?: Record<string, unknown>): void;
  goBack?(): void;
  close?(reason?: ICloseReason): void;
  openMiniApp?(miniAppId: string, params?: Record<string, unknown>): void;
  /** Открыть страницу оболочки (страница уже проверена по `HOST_PAGES`). */
  openHostPage?(page: THostPage): void;
}

export interface IBridgeHostAuth {
  getStatus?(): unknown | Promise<unknown>;
  getUser?(): unknown | Promise<unknown>;
  hasScope?(scope: string): boolean | Promise<boolean>;
}

export interface IBridgeHostSso {
  authorize?(): Promise<{
    authorizationCode: string;
    codeVerifier: string;
    expiresInSeconds: number;
  }>;
}

/**
 * Зависимости хоста, к которым мост маппит системные действия.
 * Всё опционально: то, что веб ещё не умеет (auth/sso/payments), подключается позже —
 * до тех пор RPC-действие отвечает ошибкой HANDLER_FAILED, fire-and-forget — пишет в консоль.
 */
export interface IBridgeHostDeps {
  navigation?: IBridgeHostNavigation;
  chrome?: IBridgeHostChrome;
  auth?: IBridgeHostAuth;
  sso?: IBridgeHostSso;
  analytics?: {
    track?(event: string, params?: Record<string, TBridgeScalar>): void;
  };
  location?: { getCurrentPosition?(): Promise<unknown> };
  payments?: {
    pay?(input: {
      amount: number;
      currency: string;
      description?: string;
      metadata?: Record<string, TBridgeScalar>;
    }): Promise<unknown>;
  };
  /** Показать уведомление (toast) — например, причину закрытия сервиса. */
  notify?(type: TBridgeNotificationType, message: string): void;
}

type TP<K extends keyof ISystemActionPayloads> = ISystemActionPayloads[K];

function notImplemented(action: string): never {
  throw new Error(
    `[iframe-bridge] action not implemented on web host: ${action}`
  );
}

/**
 * Выполняет одно системное действие. Для RPC-действий возвращает payload ответа,
 * для остальных — undefined. Браузерные интринсики (openExternal/share/log) реализованы
 * напрямую, доменные возможности — через прокинутые deps.
 */
export async function handleSystemAction(
  action: string,
  payload: unknown,
  deps: IBridgeHostDeps
): Promise<unknown> {
  const {
    navigation,
    chrome,
    auth,
    sso,
    analytics,
    location,
    payments,
    notify
  } = deps;

  switch (action) {
    case SYSTEM_ACTIONS.Ready:
      return undefined;

    case SYSTEM_ACTIONS.NavigationNavigate: {
      const p = payload as TP<'navigation.navigate'>;
      navigation?.navigate?.(p.route, p.params);
      return undefined;
    }
    case SYSTEM_ACTIONS.NavigationBack:
      navigation?.goBack?.();
      return undefined;
    case SYSTEM_ACTIONS.NavigationClose: {
      const p = payload as TP<'navigation.close'>;
      if (p?.reason) notify?.(p.reason.type, p.reason.message);
      navigation?.close?.(p?.reason);
      return undefined;
    }
    case SYSTEM_ACTIONS.NavigationOpenMiniApp: {
      const p = payload as TP<'navigation.openMiniApp'>;
      navigation?.openMiniApp?.(p.miniAppId, p.params);
      return undefined;
    }
    case SYSTEM_ACTIONS.NavigationOpenHostPage: {
      const p = payload as Partial<TP<'navigation.openHostPage'>> | null;
      // Неизвестная страница — ошибка RPC, а не тихий no-op: сервис узнаёт, что перехода
      // не будет, и может предложить другой путь.
      if (!isHostPage(p?.page)) throw new Error('host page is not available');
      if (!navigation?.openHostPage) return notImplemented(action);
      navigation.openHostPage(p.page);
      return undefined;
    }
    case SYSTEM_ACTIONS.NavigationSetCanGoBack: {
      const p = payload as TP<'navigation.setCanGoBack'>;
      chrome?.setCanGoBack?.(p.value);
      return undefined;
    }

    case SYSTEM_ACTIONS.AuthGetStatus:
      return auth?.getStatus ? auth.getStatus() : notImplemented(action);
    case SYSTEM_ACTIONS.AuthGetUser:
      return auth?.getUser ? auth.getUser() : notImplemented(action);
    case SYSTEM_ACTIONS.AuthHasScope: {
      const p = payload as TP<'auth.hasScope'>;
      return auth?.hasScope ? auth.hasScope(p.scope) : notImplemented(action);
    }

    case SYSTEM_ACTIONS.SsoAuthorize:
      return sso?.authorize ? sso.authorize() : notImplemented(action);
    case SYSTEM_ACTIONS.SaveCity: {
      const p = payload as TP<'city.save'>;
      safeLocalStorage.set(
        AUTO_CREDIT_CITY_STORAGE_KEY,
        JSON.stringify({ uuid: p.uuid, name: p.name })
      );
      return undefined;
    }

    case SYSTEM_ACTIONS.LocationGetCurrentPosition:
      return location?.getCurrentPosition
        ? location.getCurrentPosition()
        : notImplemented(action);

    case SYSTEM_ACTIONS.PaymentsPay: {
      const p = payload as TP<'payments.pay'>;
      return payments?.pay ? payments.pay(p) : notImplemented(action);
    }

    case SYSTEM_ACTIONS.AnalyticsTrack: {
      const p = payload as TP<'analytics.track'>;
      analytics?.track?.(p.event, p.params);
      return undefined;
    }

    case SYSTEM_ACTIONS.UiSetTitle: {
      const p = payload as TP<'ui.setTitle'>;
      chrome?.setTitle?.(p.title);
      return undefined;
    }
    case SYSTEM_ACTIONS.UiSetTabBarVisible: {
      const p = payload as TP<'ui.setTabBarVisible'>;
      chrome?.setTabBarVisible?.(p.value);
      return undefined;
    }
    case SYSTEM_ACTIONS.UiSetTabBar: {
      const p = payload as TP<'ui.setTabBar'>;
      chrome?.setTabBar?.(p);
      return undefined;
    }
    case SYSTEM_ACTIONS.UiOpenExternal: {
      const p = payload as TP<'ui.openExternal'>;
      window.open(p.url, '_blank', 'noopener,noreferrer');
      return undefined;
    }
    case SYSTEM_ACTIONS.UiShare: {
      const p = payload as TP<'ui.share'>;
      if (typeof navigator !== 'undefined' && navigator.share) {
        await navigator
          .share({ text: p.message, title: p.title, url: p.url })
          .catch(() => undefined);
      }
      return undefined;
    }
    case SYSTEM_ACTIONS.UiHaptics:
      // На вебе тактильной отдачи нет — no-op.
      return undefined;

    case SYSTEM_ACTIONS.Log: {
      const p = payload as TP<'log'>;
      // eslint-disable-next-line no-console
      console[p.level ?? 'log'](`[iframe] ${p.message}`, p.data);
      return undefined;
    }

    default:
      return undefined;
  }
}
